// My Shop › Products › Customer View: the Seller chooses how the products of
// THIS boutique are shown — Shop Product Grid (historical) or Infinite Scroll,
// per device. Saved on the boutique (PUT /shops/:id/product-display); the
// server checks ownership and the Developer authorization.
import React, { useEffect, useState } from "react";
import { FaDesktop, FaMobileAlt, FaTablet } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";
import { saveShopProductDisplay } from "../auth/apiShops";
import { PRODUCT_DISPLAY_DEVICES, normalizeProductDisplay } from "./productDisplay";
import "./ShopProductDisplaySettings.css";

const text = elakayAutoText;
const DEVICE_META = {
  desktop: { icon: FaDesktop, key: "h_spd_device_desktop", fallback: "Desktop / PC" },
  tablet: { icon: FaTablet, key: "h_spd_device_tablet", fallback: "Tablet" },
  mobile: { icon: FaMobileAlt, key: "h_spd_device_mobile", fallback: "Mobile" },
};
const modeLabel = (mode) => (mode === "infinite" ? text("h_spd_mode_infinite", "Infinite Scroll") : text("h_spd_mode_grid", "Shop Product Grid"));

const ModeChoice = ({ name, value, onChange, disabled, label }) => (
  <fieldset className="spd-choice" disabled={disabled}>
    <legend className="spd-choice__legend">{label}</legend>
    <div className="spd-choice__options">
      {["grid", "infinite"].map((mode) => {
        const id = `${name}-${mode}`;
        return (
          <span key={mode} className={`spd-choice__option ${value === mode ? "is-active" : ""}`}>
            <input id={id} type="radio" name={name} value={mode} checked={value === mode} onChange={() => onChange(mode)} />
            <label htmlFor={id}>{modeLabel(mode)}</label>
          </span>
        );
      })}
    </div>
  </fieldset>
);

const ShopProductDisplaySettings = ({ shopId, stored, allowed, previewDevice, onPreviewDeviceChange, onDraftChange, onSaved, currentDevice }) => {
  const [draft, setDraft] = useState(() => normalizeProductDisplay(stored));
  const [status, setStatus] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);
  const storedKey = JSON.stringify(normalizeProductDisplay(stored));

  // Another boutique / a fresh server value replaces the draft.
  useEffect(() => {
    setDraft(JSON.parse(storedKey));
    setStatus({ type: "", message: "" });
  }, [storedKey, shopId]);

  const dirty = JSON.stringify(draft) !== storedKey;
  useEffect(() => { onDraftChange?.(dirty ? draft : null); }, [dirty, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    setStatus({ type: "", message: "" });
    setDraft((current) => ({ ...current, ...patch, devices: { ...current.devices, ...(patch.devices || {}) } }));
  };

  if (!allowed) {
    return (
      <section className="spd-panel is-locked" aria-labelledby="spd-title">
        <h3 id="spd-title" className="spd-title">{text("h_spd_title", "Product display")}</h3>
        <p className="spd-muted" role="status">{text("h_spd_locked", "Product display customization is not enabled by Elakay. Your shop uses Shop Product Grid; your saved choices are kept.")}</p>
      </section>
    );
  }

  const save = async () => {
    if (!shopId) return;
    setSaving(true);
    setStatus({ type: "", message: "" });
    try {
      const result = await saveShopProductDisplay(shopId, draft);
      const saved = normalizeProductDisplay(result?.productDisplay || draft);
      onSaved?.(saved);
      setStatus({ type: "success", message: text("h_spd_saved", "Product display saved.") });
    } catch (error) {
      setStatus({ type: "error", message: error?.code === "PRODUCT_DISPLAY_DISABLED"
        ? text("h_spd_disabled_now", "Customization has just been disabled by Elakay.")
        : (error?.message || text("h_spd_save_error", "The product display could not be saved.")) });
    } finally {
      setSaving(false);
    }
  };

  const off = !draft.customizationEnabled;
  return (
    <section className="spd-panel" aria-labelledby="spd-title">
      <div className="spd-header">
        <div>
          <h3 id="spd-title" className="spd-title">{text("h_spd_title", "Product display")}</h3>
          <p className="spd-muted">{text("h_spd_desc", "Only the product list of this shop changes; header, sliders, banners and the other sections stay the same.")}</p>
        </div>
        <span className="spd-active" role="status">
          {text("h_spd_active_now", "Now on {device}: {mode}", {
            device: text(DEVICE_META[currentDevice]?.key, DEVICE_META[currentDevice]?.fallback),
            mode: modeLabel(off ? "grid" : draft.devices[currentDevice]),
          })}
        </span>
      </div>

      <label className="spd-switch" htmlFor="spd-enabled">
        <input id="spd-enabled" type="checkbox" role="switch" checked={draft.customizationEnabled} onChange={(event) => update({ customizationEnabled: event.target.checked })} />
        <span>{text("h_spd_switch", "Customize the product display")}</span>
      </label>

      <ModeChoice
        name="spd-default"
        label={text("h_spd_default_mode", "Display mode")}
        value={draft.defaultMode}
        disabled={off}
        onChange={(mode) => update({ defaultMode: mode, devices: { desktop: mode, tablet: mode, mobile: mode } })}
      />

      <div className="spd-devices">
        {PRODUCT_DISPLAY_DEVICES.map((device) => {
          const meta = DEVICE_META[device];
          const Icon = meta.icon;
          return (
            <ModeChoice
              key={device}
              name={`spd-${device}`}
              label={<><Icon aria-hidden="true" /> {text(meta.key, meta.fallback)}</>}
              value={draft.devices[device]}
              disabled={off}
              onChange={(mode) => update({ devices: { [device]: mode } })}
            />
          );
        })}
      </div>

      <div className="spd-footer">
        <div className="spd-preview" role="group" aria-label={text("h_spd_preview", "Preview as")}>
          <span>{text("h_spd_preview", "Preview as")}</span>
          {[null, ...PRODUCT_DISPLAY_DEVICES].map((device) => (
            <button
              key={device || "auto"}
              type="button"
              className={`spd-preview__btn ${previewDevice === device ? "is-active" : ""}`}
              aria-pressed={previewDevice === device}
              onClick={() => onPreviewDeviceChange?.(device)}
            >
              {device ? text(DEVICE_META[device].key, DEVICE_META[device].fallback) : text("h_spd_preview_auto", "This screen")}
            </button>
          ))}
        </div>
        <button type="button" className="spd-save" disabled={!dirty || saving} onClick={save}>
          {saving ? text("h_spd_saving", "Saving…") : text("h_spd_save", "Save")}
        </button>
      </div>
      {dirty && <p className="spd-muted">{text("h_spd_unsaved", "Preview of unsaved changes — visitors still see the saved display.")}</p>}
      {status.message && <p className={`spd-status is-${status.type}`} role={status.type === "error" ? "alert" : "status"}>{status.message}</p>}
    </section>
  );
};

export default ShopProductDisplaySettings;
